<template>
  <div class="dialog-mask" @click.self="onCancel">
    <div class="dialog" role="dialog" aria-modal="true" :aria-labelledby="titleId" @click.stop>
      <header class="dialog-header">
        <h2 :id="titleId" class="dialog-title">{{ $t("myPage.userInfo.edit.title") }}</h2>
        <button type="button" class="close-button" :aria-label="$t('myPage.userInfo.edit.closeAriaLabel')"
          @click="onCancel">
          <icon-close class="close-icon" />
        </button>
      </header>

      <div class="dialog-body">
        <div class="field">
          <div class="field-header">
            <label class="field-label" for="user-info-name">{{ $t("myPage.userInfo.name") }}</label>
            <span class="field-count">{{ name.length }}/100</span>
          </div>
          <input id="user-info-name" v-model="name" class="field-input" :class="{ error: !!errors.name }" type="text"
            maxlength="100" :placeholder="$t('myPage.userInfo.edit.namePlaceholder')" />
          <p v-if="errors.name" class="field-error">{{ errors.name }}</p>
        </div>

        <div class="field">
          <div class="field-header">
            <label class="field-label" for="user-info-name-kana">{{ $t("myPage.userInfo.nameKana") }}</label>
            <span class="field-count">{{ nameKana.length }}/100</span>
          </div>
          <input id="user-info-name-kana" v-model="nameKana" class="field-input" :class="{ error: !!errors.nameKana }"
            type="text" maxlength="100" :placeholder="$t('myPage.userInfo.edit.nameKanaPlaceholder')" />
          <p v-if="errors.nameKana" class="field-error">{{ errors.nameKana }}</p>
        </div>

        <div class="penname-section">
          <p class="penname-title">{{ $t("myPage.userInfo.penname") }}</p>
          <p class="penname-description">{{ $t("myPage.userInfo.edit.pennameDescription") }}</p>
          <ul class="penname-list">
            <li v-for="(penname, index) in pennames" :key="`${penname}-${index}`" class="penname-chip"
              :class="{ dragging: draggingIndex === index }" draggable="true" @dragstart="onDragStart(index)"
              @dragover.prevent="onDragOver(index)" @dragend="onDragEnd">
              <icon-handle class="penname-handle" />
              <icon-star v-if="index === 0" class="penname-star" />
              <span class="penname-text">{{ penname }}</span>
              <button type="button" class="penname-remove" :aria-label="$t('common.delete')"
                @click="onRemovePenname(index)">
                <icon-close class="penname-remove-icon" />
              </button>
            </li>
          </ul>
          <p v-if="errors.penname" class="field-error">{{ errors.penname }}</p>

          <template v-if="isAddingPenname">
            <div class="field">
              <div class="field-header">
                <label class="field-label" for="user-info-new-penname">{{
                  $t("myPage.userInfo.edit.newPenname")
                }}</label>
                <span class="field-count">{{ newPenname.length }}/50</span>
              </div>
              <input id="user-info-new-penname" ref="newPennameInput" v-model="newPenname" class="field-input"
                type="text" maxlength="50" :placeholder="$t('myPage.userInfo.edit.newPennamePlaceholder')"
                @keydown.enter.prevent="onRegisterPenname" />
            </div>
            <div class="penname-add-actions">
              <button type="button" class="penname-action-button" @click="onCancelAddPenname">
                {{ $t("common.cancel") }}
              </button>
              <button type="button" class="penname-action-button primary" :disabled="!canRegisterPenname"
                @click="onRegisterPenname">
                {{ $t("myPage.userInfo.edit.register") }}
              </button>
            </div>
          </template>
          <button v-else type="button" class="add-penname-button" :disabled="isPennameListFull"
            @click="onStartAddPenname">
            {{ $t("myPage.userInfo.edit.addPenname") }}
          </button>
        </div>

        <div class="field">
          <div class="field-header">
            <label class="field-label" for="user-info-phone">{{ $t("myPage.userInfo.phone") }}</label>
            <span class="field-count">{{ phone.length }}/100</span>
          </div>
          <input id="user-info-phone" v-model="phone" class="field-input" :class="{ error: !!errors.phone }" type="tel"
            maxlength="100" :placeholder="$t('myPage.userInfo.edit.phonePlaceholder')" />
          <p v-if="errors.phone" class="field-error">{{ errors.phone }}</p>
        </div>

        <div class="field">
          <label class="field-label" for="user-info-gender">{{ $t("myPage.userInfo.gender") }}</label>
          <div class="select-wrapper">
            <select id="user-info-gender" v-model="gender" class="field-input field-select">
              <option value="">{{ $t("common.unselected") }}</option>
              <option v-for="option in genderOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <icon-chevron-down class="select-chevron" />
          </div>
        </div>

        <div class="field">
          <label class="field-label" for="user-info-birthdate">{{ $t("myPage.userInfo.birthdate") }}</label>
          <input type="date" id="user-info-birthdate" v-model="birthdate" class="field-input"
            :class="{ error: !!errors.birthdate }" />
          <p v-if="errors.birthdate" class="field-error">{{ errors.birthdate }}</p>
        </div>
      </div>

      <footer class="dialog-footer">
        <button type="button" class="footer-button primary" :disabled="saving" @click="onSubmit">
          {{ $t("myPage.userInfo.update") }}
        </button>
        <button type="button" class="footer-button" :disabled="saving" @click="onCancel">
          {{ $t("common.cancel") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { z } from "zod";
import { NolaPfContestClient } from "@/lib/clients";
import { Gender } from "@/lib/models/nolaContest";
import { NolaPfContestProfile, UpdateNolaPfContestProfileInput } from "@/lib/models/nolaPfContest";
import { buildDisplayedPennames, splitPennamesForSave } from "@/lib/utils/pennameList";
import IconClose from "~icons/figma/Close";
import IconHandle from "~icons/figma/Handle";
import IconChevronDown from "~icons/figma/ChevronDown";
import IconStar from "~icons/figma/Star";

const nolaPfContestClient = new NolaPfContestClient();

/** CP の上限 10 件に、先頭の NP 1 件を加えた一覧の上限。 */
const MAX_PENNAMES = 11;

function formatPhone(phone?: string): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function toDigits(phone: string): string {
  return phone.replace(/\D/g, "");
}

function toDateInputValue(birthDate?: string): string {
  if (!birthDate) return "";
  const matched = birthDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!matched) return "";
  return `${matched[1]}-${matched[2]}-${matched[3]}`;
}

const UNSELECTED_GENDER = 0;

function toGenderSelectValue(gender?: number | null): string {
  if (gender === Gender.male || gender === Gender.female || gender === Gender.other) {
    return String(gender);
  }
  return "";
}

function toGenderPayload(gender: string): number {
  if (gender === String(Gender.male) || gender === String(Gender.female) || gender === String(Gender.other)) {
    return Number(gender);
  }
  return UNSELECTED_GENDER;
}

function isIsoDate(value: string): boolean {
  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!matched) return false;
  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    IconClose,
    IconHandle,
    IconChevronDown,
    IconStar,
  },
  props: {
    profile: {
      type: Object as PropType<NolaPfContestProfile | null>,
      default: null,
    },
    nolaPenname: {
      type: String as PropType<string | null>,
      default: "",
    },
  },
  data() {
    const { profile } = this;
    return {
      name: profile?.name ?? "",
      nameKana: profile?.nameKana ?? "",
      pennames: buildDisplayedPennames(this.nolaPenname, profile?.pennames),
      phone: formatPhone(profile?.phone),
      gender: toGenderSelectValue(profile?.gender),
      birthdate: toDateInputValue(profile?.birthDate),
      newPenname: "",
      isAddingPenname: false,
      draggingIndex: null as number | null,
      saving: false,
      errors: {} as Record<string, string>,
      titleId: "user-info-edit-title",
    };
  },
  computed: {
    genderOptions() {
      return [
        { value: String(Gender.male), label: this.$t("common.gender.male") as string },
        { value: String(Gender.female), label: this.$t("common.gender.female") as string },
        { value: String(Gender.other), label: this.$t("common.gender.other") as string },
      ];
    },
    isPennameListFull() {
      return this.pennames.length >= MAX_PENNAMES;
    },
    canRegisterPenname() {
      return this.newPenname.trim().length > 0 && !this.isPennameListFull;
    },
    formattedPayload(): UpdateNolaPfContestProfileInput {
      return {
        name: this.name.trim(),
        nameKana: this.nameKana.trim(),
        phone: toDigits(this.phone),
        gender: toGenderPayload(this.gender),
        birthDate: this.birthdate,
      };
    },
  },
  mounted() {
    document.body.style.overflow = "hidden";
  },
  beforeDestroy() {
    document.body.style.overflow = "";
  },
  methods: {
    onCancel() {
      if (this.saving) return;
      this.$emit("close");
    },
    onStartAddPenname() {
      this.isAddingPenname = true;
      this.newPenname = "";
      this.$nextTick(() => {
        const input = this.$refs.newPennameInput as HTMLInputElement | undefined;
        if (input) input.focus();
      });
    },
    onCancelAddPenname() {
      this.isAddingPenname = false;
      this.newPenname = "";
    },
    onRegisterPenname() {
      const value = this.newPenname.trim();
      if (!value || this.pennames.length >= MAX_PENNAMES) return;
      if (this.pennames.includes(value)) {
        this.onCancelAddPenname();
        return;
      }
      this.pennames = [...this.pennames, value];
      this.onCancelAddPenname();
    },
    onRemovePenname(index: number) {
      this.pennames = this.pennames.filter((_, i) => i !== index);
    },
    onDragStart(index: number) {
      this.draggingIndex = index;
    },
    onDragOver(index: number) {
      if (this.draggingIndex === null || this.draggingIndex === index) return;
      const list = [...this.pennames];
      const [item] = list.splice(this.draggingIndex, 1);
      list.splice(index, 0, item);
      this.pennames = list;
      this.draggingIndex = index;
    },
    onDragEnd() {
      this.draggingIndex = null;
    },
    validate(): boolean {
      const nameKanaRegExp = /^[\u3040-\u309F\u30FC\s]+$/;
      const phoneRegExp = /^0\d{9,10}$/;
      const nameKanaError = this.$t("entryProfile.validation.nameKana") as string;
      const phoneError = this.$t("entryProfile.validation.phone") as string;

      const userSchema = z.object({
        name: z.string().max(100),
        nameKana: z
          .string()
          .max(100)
          .refine((value) => !value || nameKanaRegExp.test(value), nameKanaError),
        penname: z.array(z.string().min(1).max(100)).max(MAX_PENNAMES),
        phone: z.string().refine((value) => !value || phoneRegExp.test(value), phoneError),
        gender: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(9)]),
        birthdate: z
          .string()
          .optional()
          .refine((date) => !date || isIsoDate(date)),
      });

      const result = userSchema.safeParse({
        name: this.name.trim(),
        nameKana: this.nameKana.trim(),
        penname: this.pennames,
        phone: toDigits(this.phone),
        gender: toGenderPayload(this.gender),
        birthdate: this.birthdate || undefined,
      });
      const errors: Record<string, string> = {};
      if (!result.success) {
        result.error.issues.forEach((issue) => {
          const path = issue.path.join(".");
          if (path && !errors[path]) {
            errors[path] = issue.message;
          }
        });
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async onSubmit() {
      if (this.saving) return;
      if (!this.validate()) return;

      this.saving = true;
      try {
        const { nolaPenname, contestPennames } = splitPennamesForSave(this.pennames);
        const result = await nolaPfContestClient.updateProfile({
          ...this.formattedPayload,
          pennames: contestPennames,
        });
        this.$emit("updated", result);

        if (nolaPenname !== (this.nolaPenname ?? "")) {
          try {
            await this.$store.dispatch("userModule/updateUser", nolaPenname);
          } catch (error) {
            // Nola 側だけ失敗したことが分かるログを出す（ユーザー向けのエラーは出さない）
            console.error("Failed to update Nola penname:", error);
            return;
          }
        }

        this.$notify({
          title: this.$t("common.saved") as string,
          type: "success",
        });
        this.$emit("close");
      } catch (error) {
        console.error("Failed to update contest profile:", error);
        this.$notify({
          title: this.$t("myPage.userInfo.edit.updateError") as string,
          type: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
});

interface Props {
  profile: NolaPfContestProfile | null;
  nolaPenname: string | null;
}

interface Data {
  name: string;
  nameKana: string;
  pennames: string[];
  phone: string;
  gender: string;
  birthdate: string;
  newPenname: string;
  isAddingPenname: boolean;
  draggingIndex: number | null;
  saving: boolean;
  errors: Record<string, string>;
  titleId: string;
}

interface Computed {
  genderOptions: Array<{ value: string; label: string }>;
  isPennameListFull: boolean;
  canRegisterPenname: boolean;
  formattedPayload: UpdateNolaPfContestProfileInput;
}

interface Methods {
  onCancel(): void;
  onStartAddPenname(): void;
  onCancelAddPenname(): void;
  onRegisterPenname(): void;
  onRemovePenname(index: number): void;
  onDragStart(index: number): void;
  onDragOver(index: number): void;
  onDragEnd(): void;
  validate(): boolean;
  onSubmit(): Promise<void>;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  max-height: calc(100vh - 40px);
  overflow: hidden;
  border-radius: var(--radius-lg);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.dialog-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-height: 32px;
  padding: 16px 48px 8px 16px;
}

.dialog-title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  text-align: center;
  color: var(--color-text-normal);
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--color-text-normal);
  cursor: pointer;

  @include hover-overlay;
}

.close-icon {
  font-size: 16px;
}

.dialog-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 16px 16px;

  >*+* {
    margin-top: 24px;
  }
}

.field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.field-label {
  display: block;
  margin: 0 0 0 4px;
  font-size: var(--font-size-xs);
  line-height: 1.5;
  color: var(--color-text-normal);
}

.field>.field-label {
  margin-bottom: 4px;
}

.field-count {
  font-size: var(--font-size-xs);
  line-height: 1.5;
  color: var(--color-text-normal);
}

.field-input {
  display: block;
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border: none;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  line-height: 1.5;
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-text-low);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &.error {
    box-shadow: 0 0 0 1px var(--color-warning);
  }
}

.field-select {
  appearance: none;
  padding-right: 32px;
}

.select-wrapper {
  position: relative;
}

.select-chevron {
  position: absolute;
  top: 50%;
  right: 12px;
  font-size: 12px;
  color: var(--color-text-normal);
  pointer-events: none;
  transform: translateY(-50%);
}

.field-error {
  margin: 4px 4px 0;
  font-size: var(--font-size-xs);
  color: var(--color-warning);
}

.penname-section {
  margin-top: 0;
}

.penname-title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  color: var(--color-text-normal);
}

.penname-description {
  margin: 4px 0 0;
  font-size: var(--font-size-sm);
  line-height: 1.5;
  color: var(--color-text-low);
}

.penname-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;
}

.penname-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px 0 8px;
  border-radius: var(--radius-rounded);
  background-color: var(--color-background-invert);
  color: var(--color-text-invert);
  cursor: grab;

  &.dragging {
    opacity: 0.6;
  }
}

.penname-handle {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--color-text-invert);
}

.penname-star {
  flex-shrink: 0;
  font-size: var(--font-size-md);
  color: var(--color-caution);
}

.penname-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  white-space: nowrap;
}

.penname-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-invert);
  cursor: pointer;
}

.penname-remove-icon {
  font-size: 8px;
}

.add-penname-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  margin-top: 16px;
  padding: 0 16px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.penname-add-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.penname-action-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 16px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &.primary {
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.dialog-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
  padding: 8px 16px 24px;
}

.footer-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &.primary {
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

:deep(.date-picker-wrapper) {
  display: block;
  width: 100%;
}

:deep(.date-input) {
  display: block;
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border: none;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  line-height: 1.5;
  box-sizing: border-box;

  &::placeholder {
    color: var(--color-text-low);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }

  &.error {
    box-shadow: 0 0 0 1px var(--color-warning);
  }
}

:deep(.vdp-datepicker__calendar) {
  z-index: 1;
}

@include mq("sp") {
  .dialog-mask {
    padding: 16px;
    align-items: flex-end;
  }

  .dialog {
    max-width: none;
    max-height: calc(100vh - 32px);
  }
}
</style>
